import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { Users, UserCheck, UserX, Calendar, Search, Download, Eye } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString();

function StatCard({ label, value, sub, color = 'blue', icon: Icon }) {
  const colors = {
    blue:   { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    icon: 'text-blue-500'    },
    green:  { bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700',   icon: 'text-green-500'   },
    purple: { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700',  icon: 'text-purple-500'  },
    amber:  { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   icon: 'text-amber-500'   },
    teal:   { bg: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700',    icon: 'text-teal-500'    },
    indigo: { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  icon: 'text-indigo-500'  },
  }[color];

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-xl p-4`}>
      <div className="flex items-center gap-3 mb-2">
        {Icon && <Icon className={`w-5 h-5 ${colors.icon}`} />}
        <p className={`text-sm font-medium ${colors.text}`}>{label}</p>
      </div>
      <p className={`text-3xl font-bold ${colors.text}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-sm">
      <p className="text-slate-600 text-xs">{label}</p>
      <p className="text-slate-800 font-semibold">{payload[0].value}</p>
    </div>
  );
};

const SELECT_CLS = 'px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';

export default function DataQualityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    apiService.getDataQuality()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="text-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-slate-500">Loading data quality report…</p>
    </div>
  );

  if (!data) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600">Failed to load data quality report</p>
    </div>
  );

  const { holders, family, totalPeople, ageDistribution, profileCompleteness, allPeople } = data;
  const completePct   = holders.total > 0 ? Math.round((profileCompleteness.complete / holders.total) * 100) : 0;
  const incompletePct = 100 - completePct;

  const pieDataCompleteness = [
    { name: 'Complete',   value: profileCompleteness.complete },
    { name: 'Incomplete', value: profileCompleteness.incomplete },
  ];
  const PIE_COLORS_C = ['#22c55e', '#f59e0b'];

  const barDataMissing = [
    { name: 'Missing DOB',     count: holders.missingDob },
    { name: 'Missing Phone',   count: holders.missingPhone },
    { name: 'Missing Email',   count: holders.missingEmail },
    { name: 'Missing Address', count: holders.missingAddress },
  ].filter(d => d.count > 0);
  const BAR_COLORS = ['#ef4444', '#3b82f6', '#8b5cf6', '#f97316'];

  const pieDataAge = [
    { name: '21+ (Contributing)', value: ageDistribution.aged21plus },
    { name: 'Under 21',           value: ageDistribution.under21 },
    { name: 'No DOB',             value: ageDistribution.noDob },
  ].filter(d => d.value > 0);
  const PIE_COLORS_A = ['#06b6d4', '#8b5cf6', '#f97316'];

  // Filter combined people list
  const people = allPeople || [];
  const filtered = people.filter(m => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      m.name?.toLowerCase().includes(q) ||
      m.ref?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.relation?.toLowerCase().includes(q);

    const matchType =
      typeFilter === 'all'    ? true :
      typeFilter === 'holder' ? m.memberType === 'holder' :
      typeFilter === 'family' ? m.memberType === 'family' : true;

    const matchStatus =
      statusFilter === 'all'             ? true :
      statusFilter === 'complete'        ? m.isComplete :
      statusFilter === 'incomplete'      ? !m.isComplete :
      statusFilter === 'missing_dob'     ? !m.hasDob :
      statusFilter === 'missing_phone'   ? !m.phone :
      statusFilter === 'missing_email'   ? !m.email :
      statusFilter === 'missing_address' ? !m.address :
      statusFilter === 'turning_21'      ? m.flaggedTurning21 :
      statusFilter === 'pending_approval'? m.pendingApproval : true;

    const age = m.age;
    const matchAge =
      ageFilter === 'all'    ? true :
      ageFilter === '21plus' ? (age !== null && age >= 21) :
      ageFilter === 'under21'? (age !== null && age < 21) :
      ageFilter === 'no_dob' ? age === null :
      ageFilter === '0-10'   ? (age !== null && age >= 0  && age <= 10) :
      ageFilter === '11-17'  ? (age !== null && age >= 11 && age <= 17) :
      ageFilter === '18-20'  ? (age !== null && age >= 18 && age <= 20) :
      ageFilter === '21-40'  ? (age !== null && age >= 21 && age <= 40) :
      ageFilter === '41-60'  ? (age !== null && age >= 41 && age <= 60) :
      ageFilter === '61plus' ? (age !== null && age >= 61) : true;

    return matchSearch && matchType && matchStatus && matchAge;
  });

  const tableTitle =
    typeFilter === 'holder' ? `Account Holders (${filtered.length})` :
    typeFilter === 'family' ? `Family Members (${filtered.length})` :
    `All Members (${filtered.length})`;

  const exportCSV = () => {
    const header = ['Ref', 'Name', 'Type', 'Relationship', 'Age', 'Phone', 'Email', '£/mo', 'Profile', 'Missing Fields', 'Alert'];
    const rows = filtered.map(m => [
      m.ref,
      m.name,
      m.memberType === 'holder' ? 'Account Holder' : 'Family Member',
      m.relation,
      m.age !== null ? m.age : 'No DOB',
      m.phone || '',
      m.email || '',
      m.monthlyAmount !== null ? m.monthlyAmount : '',
      m.isComplete ? 'Complete' : 'Incomplete',
      (m.missingFields || []).join('; '),
      m.pendingApproval ? 'Needs Approval' : m.flaggedTurning21 ? 'Turning 21' : '',
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Account Holders" value={fmt(holders.total)}  color="blue"   icon={Users} />
        <StatCard label="Family Members"  value={fmt(family.total)}   color="purple" icon={Users} />
        <StatCard label="Total People"    value={fmt(totalPeople)}    color="indigo" icon={Users} />
        <StatCard label="Complete Profiles" value={`${completePct}%`} sub={`${fmt(profileCompleteness.complete)} holders`} color="green" icon={UserCheck} />
        <StatCard label="Incomplete Profiles" value={`${incompletePct}%`} sub={`${fmt(profileCompleteness.incomplete)} holders`} color="amber" icon={UserX} />
        <StatCard label="Aged 21+" value={fmt(ageDistribution.aged21plus)}
          sub={`${fmt(ageDistribution.under21)} under 21 · ${fmt(ageDistribution.noDob)} no DOB`}
          color="teal" icon={Calendar} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4 text-sm">Profile Completeness</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pieDataCompleteness} cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                dataKey="value" paddingAngle={3}>
                {pieDataCompleteness.map((_, i) => <Cell key={i} fill={PIE_COLORS_C[i]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {pieDataCompleteness.map((d, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS_C[i] }}></div>
                {d.name}: {d.value}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4 text-sm">Missing Fields Breakdown</h3>
          {barDataMissing.length === 0 ? (
            <div className="text-center py-8 text-green-600 font-medium text-sm">All profiles complete!</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={barDataMissing} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {barDataMissing.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4 text-sm">Age Distribution</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pieDataAge} cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                dataKey="value" paddingAngle={3}>
                {pieDataAge.map((_, i) => <Cell key={i} fill={PIE_COLORS_A[i]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {pieDataAge.map((d, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS_A[i] }}></div>
                {d.name}: {d.value}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* All Members table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <h3 className="font-semibold text-slate-800 text-sm flex-1">{tableTitle}</h3>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search name, ref…"
                className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs w-40 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={SELECT_CLS}>
              <option value="all">All Types</option>
              <option value="holder">Account Holders</option>
              <option value="family">Family Members</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={SELECT_CLS}>
              <option value="all">All Statuses</option>
              <option value="complete">Complete</option>
              <option value="incomplete">Incomplete</option>
              <option value="missing_dob">Missing DOB</option>
              <option value="missing_phone">Missing Phone</option>
              <option value="missing_email">Missing Email</option>
              <option value="missing_address">Missing Address</option>
              <option disabled>──────────────</option>
              <option value="turning_21">Turning 21 (30-day alert)</option>
              <option value="pending_approval">Auto-created, Approval Needed</option>
            </select>
            <select value={ageFilter} onChange={e => setAgeFilter(e.target.value)} className={SELECT_CLS}>
              <option value="all">All Ages</option>
              <option value="21plus">21+ (Contributing)</option>
              <option value="under21">Under 21</option>
              <option value="no_dob">No DOB</option>
              <option value="0-10">0–10</option>
              <option value="11-17">11–17</option>
              <option value="18-20">18–20</option>
              <option value="21-40">21–40</option>
              <option value="41-60">41–60</option>
              <option value="61plus">61+</option>
            </select>
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium transition-colors">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Ref</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">Relationship</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Age</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden xl:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">£/mo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Profile</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">Missing</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.slice(0, 150).map(m => (
                <tr key={`${m.memberType}-${m.id}`} className={`hover:bg-slate-50 transition-colors ${m.pendingApproval ? 'bg-yellow-50/60' : m.flaggedTurning21 ? 'bg-teal-50/40' : ''}`}>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">{m.ref}</td>
                  <td className="px-4 py-3 font-medium text-slate-800 text-sm">
                    {m.name}
                    {m.flaggedTurning21  && <span className="ml-2 px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">Turning 21</span>}
                    {m.pendingApproval  && <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Needs Approval</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {m.memberType === 'holder'
                      ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Holder</span>
                      : <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">Family</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell">{m.relation}</td>
                  <td className="px-4 py-3 text-xs">
                    {m.hasDob
                      ? <span className="text-slate-700 font-medium">{m.age}</span>
                      : <span className="text-red-400">No DOB</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 hidden lg:table-cell">
                    {m.phone || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 hidden xl:table-cell">
                    {m.email || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-medium hidden lg:table-cell">
                    {m.monthlyAmount !== null
                      ? <span className="text-green-700">£{m.monthlyAmount}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {m.isComplete
                      ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Complete</span>
                      : <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Incomplete</span>}
                  </td>
                  <td className="px-4 py-3 text-xs hidden sm:table-cell">
                    {m.missingFields.length > 0
                      ? <span className="text-amber-600 font-medium">⚠ {m.missingFields.length}</span>
                      : <span className="text-green-500">✓</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/members/${m.holderId}`)}
                      className="text-slate-400 hover:text-blue-600 transition-colors" title="View profile">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center py-8 text-slate-400 text-sm">No members match the filters</td></tr>
              )}
              {filtered.length > 150 && (
                <tr><td colSpan={11} className="text-center py-3 text-slate-400 text-xs">
                  Showing 150 of {filtered.length} — use filters to narrow, or export CSV for the full list
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
